// @flow
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from './validation/runtime-checks/index.js';
import type { ChildrenArray } from 'react';
import { productColorSunflower } from './designSystem.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';

export function PseudoCodeBlock(props: {|
  children: ChildrenArray<React$Element<typeof LineOfCode>>,
  noLineNumbering?: boolean,
  ...CompilerInjectedProps,
|}) {
  const { children, noLineNumbering = false, _performValidation } = props;
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    PseudoCodeBlock,
  );

  const lines = React.Children.toArray(children);

  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;

    const [firstLine] = lines;
    const { indentLevel } = firstLine.props;
    if (indentLevel !== undefined && indentLevel !== 0) {
      addErrors([
        {
          message: `PseudoCodeBlock must start with a LineofCode with indentLevel={0}`,
          ...locations.PseudoCodeBlock,
        },
      ]);
    }
  });
  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;

    // we don't want to look at the first element.
    for (const [i, line] of lines.slice(1).entries()) {
      if (
        (lines[i + 1].props.indentLevel ?? 0) -
          (lines[i].props.indentLevel ?? 0) >
        1
      ) {
        addErrors([
          {
            message: `A LineOfCode's indentLevel can only be at most 1 greater than the previous LineOfCode's indent level`,
            ...line.props._sourceLocations?.indentLevel,
          },
        ]);
      }
    }
  });
  if (failedValidation) {
    return null;
  }
  return (
    <div
      style={{
        textAlign: 'left',
        backgroundColor: productColorSunflower.seashell,
        fontFamily: 'Menlo, Consolas, Roboto Mono, Cousine, monospace',
        fontSize: 16,
        padding: '12px 9px',
        color: productColorSunflower.grey,
        border: `1px solid ' ${productColorSunflower.iron}`,
        borderRadius: 4,
        margin: 24,
      }}
    >
      {React.Children.map(children, (child, _lineNumber) =>
        noLineNumbering
          ? child
          : React.cloneElement(child, {
              _lineNumber,
            }),
      )}
      <div style={{ height: 20 }} />
    </div>
  );
}
PseudoCodeBlock.spec = {
  children: t.childrenArray(t.elementType(LineOfCode)),
  noLineNumbering: t.optional(t.boolean),
};

export function LineOfCode(props: {|
  code: string,
  indentLevel?: number,
  _lineNumber?: number,
  ...CompilerInjectedProps,
|}) {
  const {
    code,
    // This prop is proving a bit tricky, you can put relatively low numbers here
    // and have it break layout, but I'm not too sure how to implement checks for
    // that specifically.
    // The current set up enforces only a single jump up on *subsequent* lines.
    // (not adjacent cos you can a have a multiply indented block that jumps up
    // two or more levels sometimes.)
    indentLevel = 0,
    _lineNumber = null,
    _performValidation,
  } = props;
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    LineOfCode,
  );
  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;

    if (code && code.match(/(^\s|\s$)/) !== null) {
      addErrors([
        {
          message: `LineOfCode cannot have leading or trailing whitespace. To introduce indentation use the indentLevel prop.`,
          ...locations.code,
        },
      ]);
    }
  });
  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;

    if (indentLevel < 0 || !Number.isInteger(indentLevel)) {
      addErrors([
        {
          message: `indentLevel must be a non-negative integer.`,
          ...locations.indentLevel,
        },
      ]);
    }
  });

  if (failedValidation) return null;
  return (
    <div
      style={{
        margin: 5,
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <div
        style={{
          color: productColorSunflower.grey90,
          whiteSpace: 'pre-wrap',
          flexShrink: 0,
        }}
      >
        {/* hardcode width so single and double digit lineNumber have same width */}
        <span
          style={{
            width: 40,
            textAlign: 'right',
            display: 'inline-block',
            paddingRight: 16,
          }}
        >
          {_lineNumber !== null && `${_lineNumber + 1}`}
        </span>
        {'  '.repeat(indentLevel)}
      </div>
      {/* Ensure that empty lines retain their line height */}
      <div>{code === '' ? <>&nbsp;</> : code}</div>
    </div>
  );
}
LineOfCode.spec = {
  // Although slightly counter-intuitive, we don't enforce that
  // `code` is a non-emtpy string. This is due to use cases where
  // a line needs to be left empty to ask questions about what
  // should be there. e.g. "Which of the following options should
  // be at line 4 to complete blah algorithm?"
  code: t.string,
  indentLevel: t.optional(t.number),
};
